import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminHOC from "../../components/shared/HOC/AdminHOC";
import "./Quiz.css";
import { Input, message, Radio, Row } from "antd";
import { Col } from "antd/lib";
import InputWrapper from "../../components/shared/Input/Input-Wrapper";
import CustomInput from "../../components/shared/Input/Custom-Input";
import PlusMinusSelector from "../../components/shared/Input/PlusMinusSelector";
import CustomRadio from "../../components/shared/Radio/RadioSelector";
import deleteIcon from "../../assets/icons/Delete.svg";
import AddIcon from "../../assets/icons/Add.svg";
import CustomInputOption from "../../components/shared/Input/CustomInputOption";
import CustomButton from "../../components/shared/button/CustomButton";
import CustomSwitch from "../../components/shared/Switch/CustomSwitch";
import RedBack from "../../assets/icons/RedBack.svg";
import RightBack from "../../assets/icons/RightBack.svg";
import ArrowDown from "../../assets/icons/ArrowDown.svg";
import ArrowUp from "../../assets/icons/ArrowUp.svg";
import ConfirmDeletePopup from "../../components/shared/confirmDeletePopup/ConfirmDeletePopup";
import { useQuiz, createBlankQuestion } from "../../hooks/managequiz/useQuiz";
import Back from "../../assets/back-btn.svg";

const Quiz = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const {
    quiz,
    quizMeta,
    setQuizMeta,
    questions,
    setQuestions,
    fetchQuestions,
    saveQuiz,
    saveQuestion,
    removeQuestion,
  } = useQuiz(courseId);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [metaErrors, setMetaErrors] = useState({
    title: "",
    description: "",
    timeLimit: "",
    passingScore: "",
    attemptsAllowed: "",
    totalQuestions: "",
  });

  const currentQuestion = questions[currentQuestionIndex];
  const isQuestionSaved = currentQuestion?.isSaved === true;

  const updateMeta = (field, value) => {
    setQuizMeta((prev) => ({ ...prev, [field]: value }));
    if (metaErrors[field]) {
      setMetaErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateQuizMeta = () => {
  const errors = {
    title: "",
    description: "",
    timeLimit: "",
    passingScore: "",
    attemptsAllowed: "",
    totalQuestions: "",
  };
  let isValid = true;

  if (!quizMeta.title?.trim()) {
    errors.title = "Quiz title is required.";
    isValid = false;
  }

  if (!quizMeta.description?.trim()) {
    errors.description = "Description is required.";
    isValid = false;
  }
  if (!quizMeta.timeLimit || quizMeta.timeLimit <= 0) {
    errors.timeLimit = "Time limit must be greater than 0.";
    isValid = false;
  }
  if (quizMeta.passingScore === undefined || quizMeta.passingScore === null || quizMeta.passingScore === ""|| quizMeta.passingScore<=0) {
    errors.passingScore = "Passing score is required.";
    isValid = false;
  }

  if (!quizMeta.attemptsAllowed || quizMeta.attemptsAllowed <= 0) {
    errors.attemptsAllowed = "At least 1 attempt must be allowed.";
    isValid = false;
  }

  if (!quizMeta.totalQuestions || quizMeta.totalQuestions <= 0) {
    errors.totalQuestions = "Must display at least 1 question.";
    isValid = false;
  }

  setMetaErrors(errors);
  return isValid;
};

  const updateCurrentQuestion = (field, value) => {
    setQuestions((prev) =>
      prev.map((q, idx) =>
        idx === currentQuestionIndex ? { ...q, [field]: value } : q
      )
    );
  };

  const handleOptionChange = (id, newText) => {
    const updated = currentQuestion.options.map((opt) =>
      opt.id === id ? { ...opt, text: newText } : opt
    );
    updateCurrentQuestion("options", updated);
  };

  const handleSelectCorrectAnswer = (id) => {
    const updated = currentQuestion.options.map((opt) => ({
      ...opt,
      isCorrect: opt.id === id,
    }));
    const correct = updated.find((opt) => opt.id === id);
    updateCurrentQuestion("options", updated);
    updateCurrentQuestion("correctAnswer", correct ? [correct.text] : []);
  };

  const handleToggleCorrectAnswer = (id) => {
    const updated = currentQuestion.options.map((opt) =>
      opt.id === id ? { ...opt, isCorrect: !opt.isCorrect } : opt
    );
    updateCurrentQuestion("options", updated);
    updateCurrentQuestion(
      "correctAnswer",
      updated.filter((o) => o.isCorrect).map((o) => o.text)
    );
  };

  const handleAddOption = () => {
    if (currentQuestion.options.length >= 5) {
      alert("Maximum 5 options allowed");
      return;
    }
    updateCurrentQuestion("options", [
      ...currentQuestion.options,
      { id: Date.now(), text: "", isCorrect: false },
    ]);
  };

  const handleDeleteOption = (id) => {
    if (currentQuestion.options.length <= 2) {
      alert("Minimum 2 options required");
      return;
    }
    const newOptions = currentQuestion.options.filter((opt) => opt.id !== id);
    updateCurrentQuestion("options", newOptions);
    updateCurrentQuestion(
      "correctAnswer",
      newOptions.filter((o) => o.isCorrect).map((o) => o.text)
    );
  };

  const handleQuestionTypeChange = (e) => {
    const typeMap = {
      single: "MCQ_SINGLE",
      multiple: "MCQ_MULTIPLE",
      descriptive: "SHORT_ANSWER",
    };
    const newType = typeMap[e.target.value];
    updateCurrentQuestion("questionType", newType);

    if (e.target.value === "descriptive") {
      updateCurrentQuestion("options", []);
      updateCurrentQuestion("correctAnswer", []);
    } else if (currentQuestion.options.length === 0) {
      updateCurrentQuestion("options", [
        { id: Date.now(), text: "", isCorrect: false },
        { id: Date.now() + 1, text: "", isCorrect: false },
      ]);
    }
  };

  const getDisplayType = () => {
    const map = {
      MCQ_SINGLE: "single",
      MCQ_MULTIPLE: "multiple",
      SHORT_ANSWER: "descriptive",
    };
    return map[currentQuestion.questionType] || "single";
  };

  const handleAddQuestion = async () => {
    if (!currentQuestion.questionText.trim()) {
      message.error("Please enter a question");
      return;
    }

    try {
      await saveQuestion(
        currentQuestion,
        isQuestionSaved,
        quiz.quizId,
        currentQuestionIndex,
        questions.length
      );

      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to save question");
    }
  };

  const handleAddNewQuestionClick = () => {
    const newQuestion = {
      id: Date.now(),
      displayId: questions.length + 1,
      questionText: "",
      questionType: "MCQ_SINGLE",
      options: [
        { id: Date.now(), text: "", isCorrect: false },
        { id: Date.now() + 1, text: "", isCorrect: false },
      ],
      correctAnswer: [],
      points: 10,
      explanation: "",
      required: false,
      isSaved: false,
    };

    setQuestions([...questions, newQuestion]);
    setCurrentQuestionIndex(questions.length);
  };

  const openDeleteModal = (question) => {
    setQuestionToDelete(question);
    setIsConfirmPopupOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!questionToDelete) return;
    try {
      await removeQuestion(questionToDelete.id, quiz.quizId);

      if (questions.length <= 1) {
        setQuestions([createBlankQuestion(1)]);
        setCurrentQuestionIndex(0);
      } else if (currentQuestionIndex >= questions.length - 1) {
        setCurrentQuestionIndex(Math.max(0, questions.length - 2));
      }
    } catch (error) {
      message.error("Failed to delete question");
    } finally {
      setIsConfirmPopupOpen(false);
      setQuestionToDelete(null);
    }
  };

  const toggleQuestionOptions = (questionId) => {
    setExpandedQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const handleSaveQuiz = () => {
    if (!validateQuizMeta()) {
      const firstErrorEl = document.querySelector(".field-error");
      if (firstErrorEl) {
        firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    saveQuiz(courseId);
  };

  const renderQuestionsContent = () => {
    if (currentQuestion.questionType === "SHORT_ANSWER") {
      return (
        <div className="question-content-wrap">
          <CustomInput
            className="short-answer-input"
            value={currentQuestion.correctAnswer}
            onChange={(e) => updateCurrentQuestion("correctAnswer", e.target.value)}
          />
          <CustomInput
            placeholder="Explanation (Optional)"
            value={currentQuestion.explanation}
            onChange={(e) => updateCurrentQuestion("explanation", e.target.value)}
          />
        </div>
      );
    }

    const isMultiple = currentQuestion.questionType === "MCQ_MULTIPLE";

    return (
      <div className="question-content-wrap">
        {currentQuestion.options.map((option, index) => (
          <Row key={option.id} gutter={10}>
            <Col span={23} style={{ paddingRight: "10px" }}>
              <CustomInputOption
                optionNumber={index + 1}
                placeholder={`Enter option ${index + 1}`}
                value={option.text}
                onChange={(e) => handleOptionChange(option.id, e.target.value)}
                onSelect={() =>
                  isMultiple
                    ? handleToggleCorrectAnswer(option.id)
                    : handleSelectCorrectAnswer(option.id)
                }
                selected={option.isCorrect}
                isMultiple={isMultiple}
              />
            </Col>
            <Col span={1}>
              <div
                onClick={() => handleDeleteOption(option.id)}
                className={`icon-btn ${currentQuestion.options.length <= 2 ? "icon-btn--disabled" : ""}`}
              >
                <img src={deleteIcon} alt="delete-icon" />
              </div>
            </Col>
          </Row>
        ))}

        <div
          onClick={handleAddOption}
          className={`add-option-row ${currentQuestion.options.length >= 5 ? "add-option-row--disabled" : ""}`}
        >
          <img src={AddIcon} alt="add" />
          <span className="add-option-label">Add Option</span>
        </div>

        <CustomInput
          placeholder="Explanation (optional)"
          value={currentQuestion.explanation}
          onChange={(e) => updateCurrentQuestion("explanation", e.target.value)}
        />
      </div>
    );
  };

  const renderExpandedQuestion = (q) => (
    <Row key={q.id}>
      <Col span={23}>
        <div className="open-quiz-layout">
          <div className="expanded-question-header">
            <div className="expanded-question-text">
              <span className="question-label-nowrap">Q {q.displayId}.</span>
              <span>{q.questionText || "No question text"}</span>
            </div>
            <img
              alt="toggle"
              src={expandedQuestions[q.id] ? ArrowUp : ArrowDown}
              className="arrow-toggle"
              onClick={() => toggleQuestionOptions(q.id)}
            />
          </div>

          {expandedQuestions[q.id] && q.questionType !== "SHORT_ANSWER" && q.options.length > 0 && (
            <div className="option-list">
              {q.options.map((option, idx) => (
                <div key={option.id} className="option-layout">
                  <span className="option-text">
                    {idx + 1}. {option.text || `Option ${idx + 1}`}
                  </span>
                  {option.isCorrect && (
                    <span className="correct-label">(Correct Answer)</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {expandedQuestions[q.id] && q.questionType === "SHORT_ANSWER" && q.correctAnswer && (
            <div className="option-list">
              <div className="option-layout">
                <span className="option-text">{q.correctAnswer}</span>
              </div>
            </div>
          )}
        </div>
      </Col>
      <Col span={1} className="delete-col">
        <div className="icon-btn icon-btn--lg" onClick={() => openDeleteModal(q)}>
          <img src={deleteIcon} alt="delete-icon" />
        </div>
      </Col>
    </Row>
  );

  return (
    <div className="quiz-outer-layout">
      <div style={{display: "flex", justifyContent: "space-between"}}>
        <span className="quiz-page-title">
          {quiz ? "Edit" : "Create"} Quiz
        </span>
        <div
          onClick={() => navigate(-1)}
        >
          <img alt="back-btn" src={Back} />
          <span>Back</span>
        </div>
      </div>

      <div className="quiz-title-layout">
        <Row>
          <Col span={12} className="quiz-meta-left">
            <InputWrapper label="Quiz Title" required>
              <CustomInput
                value={quizMeta.title}
                onChange={(e) => updateMeta("title", e.target.value)}
                placeholder="Enter quiz title"
                status={metaErrors.title ? "error" : ""}
              />
              {metaErrors.title && (
                <span className="field-error">{metaErrors.title}</span>
              )}
            </InputWrapper>

            <div className="quiz-selectors-row">
  <InputWrapper label="Time Limit (mins)" required>
    <PlusMinusSelector
      value={quizMeta.timeLimit}
      onChange={(v) => updateMeta("timeLimit", v)}
      min={0} max={180} step={1}
      status={metaErrors.timeLimit ? "error" : ""}
    />
    <span className="field-error selector-error" style={{ visibility: metaErrors.timeLimit ? "visible" : "hidden" }}>
      {metaErrors.timeLimit || "placeholder"}
    </span>
  </InputWrapper>

  <InputWrapper label="Passing Score (%)" required>
    <PlusMinusSelector
      value={quizMeta.passingScore}
      onChange={(v) => updateMeta("passingScore", v)}
      min={0} max={100} step={1}
      status={metaErrors.passingScore ? "error" : ""}
    />
    <span className="field-error selector-error" style={{ visibility: metaErrors.passingScore ? "visible" : "hidden" }}>
      {metaErrors.passingScore || "placeholder"}
    </span>
  </InputWrapper>

  <InputWrapper label="Attempts Allowed" required>
    <PlusMinusSelector
      value={quizMeta.attemptsAllowed}
      onChange={(v) => updateMeta("attemptsAllowed", v)}
      min={0} max={10} step={1}
      status={metaErrors.attemptsAllowed ? "error" : ""}
    />
    <span className="field-error selector-error" style={{ visibility: metaErrors.attemptsAllowed ? "visible" : "hidden" }}>
      {metaErrors.attemptsAllowed || "placeholder"}
    </span>
  </InputWrapper>

  <InputWrapper label="Display Questions" required>
    <PlusMinusSelector
      value={quizMeta.totalQuestions}
      onChange={(v) => updateMeta("totalQuestions", v)}
      min={0} max={50} step={1}
      status={metaErrors.totalQuestions ? "error" : ""}
    />
    <span className="field-error selector-error" style={{ visibility: metaErrors.totalQuestions ? "visible" : "hidden" }}>
      {metaErrors.totalQuestions || "placeholder"}
    </span>
  </InputWrapper>
</div>
          </Col>

          <Col span={12} className="quiz-meta-right">
            <InputWrapper label="Description" required>
              <Input.TextArea
                value={quizMeta.description}
                onChange={(e) => updateMeta("description", e.target.value)}
                className={`quiz-description-textarea ${metaErrors.description ? "ant-input-status-error" : ""}`}
                autoComplete="off"
                rows={4}
                maxLength={1000}
                status={metaErrors.description ? "error" : ""}
              />
              {metaErrors.description && (
                <span className="field-error">{metaErrors.description}</span>
              )}
            </InputWrapper>
          </Col>
        </Row>

        <Row className="quiz-toggles-row">
          <div className="toggle-group">
            <CustomSwitch value={quizMeta.showResults} onChange={(v) => updateMeta("showResults", v)} />
            <span className="toggle-label">Show Result</span>
          </div>
          <div className="toggle-group">
            <CustomSwitch value={quizMeta.randomizeQuestions} onChange={(v) => updateMeta("randomizeQuestions", v)} />
            <span className="toggle-label">Randomize Questions</span>
          </div>
          <CustomButton onClick={handleSaveQuiz}>
            {quiz ? "Update Quiz" : "Create"}
          </CustomButton>
        </Row>
      </div>

      <Row
        className="no-scrollbar quiz-questions-row"
        style={{ opacity: quiz ? 1 : 0.5 }}
      >
        {!isExpanded && (
          <Col span={21} style={{ pointerEvents: quiz ? "auto" : "none" }}>
            <div className="quiz-question-layout">
              <span className="question-counter">
                Question: {currentQuestion?.displayId || 1} of {questions.length}
              </span>

              <div className="question-input-wrap">
                <InputWrapper label="Question">
                  <CustomInput
                    value={currentQuestion.questionText}
                    onChange={(e) => updateCurrentQuestion("questionText", e.target.value)}
                    placeholder="Enter your question here"
                  />
                </InputWrapper>
              </div>

              <div className="quiz-question-selector">
                <div className="quiz-selector">
                  <Radio.Group
                    className="question-type-group"
                    onChange={handleQuestionTypeChange}
                    value={getDisplayType()}
                  >
                    <CustomRadio value="single">
                      <div className="radio-label">Single Answer MCQ</div>
                    </CustomRadio>
                    <CustomRadio value="multiple">
                      <div className="radio-label">Multiple Answer MCQ</div>
                    </CustomRadio>
                    <CustomRadio value="descriptive">
                      <div className="radio-label">Descriptive Answer</div>
                    </CustomRadio>
                  </Radio.Group>

                  <InputWrapper label="Points" required>
                    <PlusMinusSelector
                      value={currentQuestion.points}
                      onChange={(v) => updateCurrentQuestion("points", v)}
                      min={0}
                      max={180}
                    />
                  </InputWrapper>
                </div>

                <div className="questions-div">{renderQuestionsContent()}</div>
              </div>

              <div className="question-action-row">
                <div className="toggle-group">
                  <CustomSwitch
                    value={currentQuestion.required ?? false}
                    onChange={(checked) => updateCurrentQuestion("required", checked)}
                  />
                  <span className="toggle-label">Mandatory Question</span>
                </div>
                <CustomButton variant="primary" onClick={handleAddQuestion}>
                  {isQuestionSaved ? "Update Question" : "Add Question"}
                </CustomButton>
              </div>
            </div>
          </Col>
        )}

        <Col
          span={isExpanded ? 24 : 3}
          style={{ pointerEvents: quiz ? "auto" : "none" }}
        >
          <div className="sidebar-question">
            <div className="sidebar-header-quiz">
              <img
                src={isExpanded ? RightBack : RedBack}
                className="sidebar-back-btn"
                onClick={() => setIsExpanded((p) => !p)}
                alt="back"
              />
              <span className="sidebar-title">Questions</span>
            </div>

            {!isExpanded ? (
              <div className="sidebar-question-list">
                {questions.map((q, idx) => (
                  <div
                    key={q.id}
                    onClick={() => setCurrentQuestionIndex(idx)}
                    className={`sidebar-question-item ${currentQuestionIndex === idx ? "sidebar-question-item--active" : ""}`}
                  >
                    <span className={`sidebar-question-label ${currentQuestionIndex === idx ? "sidebar-question-label--active" : ""}`}>
                      Q.{q.displayId}
                    </span>
                  </div>
                ))}

                {questions[questions.length - 1]?.isSaved && (
                  <div
                    className="sidebar-question-item"
                    style={{ cursor: "pointer", border: "1px dashed #d9d9d9", textAlign: "center" }}
                    onClick={handleAddNewQuestionClick}
                  >
                    <span className="sidebar-question-label">+</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="expanded-question-list">
                {questions.filter((q) => q.isSaved).map(renderExpandedQuestion)}
              </div>
            )}
          </div>
        </Col>
      </Row>

      <ConfirmDeletePopup
        className="delete-popup"
        isOpen={isConfirmPopupOpen}
        onClose={() => { setIsConfirmPopupOpen(false); setQuestionToDelete(null); }}
        onConfirm={handleConfirmDelete}
        title="Delete Question"
        text="Are you sure you want to delete this Question?"
      />
    </div>
  );
};

Quiz.sidebar = false;

export default AdminHOC(Quiz);